import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue";
const _hoisted_1 = { class: "how-to-arrive my-4" };
const _hoisted_2 = { class: "card p-4" };
const _hoisted_3 = {
    key: 0,
    class: "mb-4"
};
const _hoisted_4 = { class: "row g-4" };
const _hoisted_5 = { class: "col-12 col-md-6" };
const _hoisted_6 = { class: "info-list" };
const _hoisted_7 = { class: "text" };
const _hoisted_8 = { class: "col-12 col-md-6" };
const _hoisted_9 = { class: "info-list" };
const _hoisted_10 = {
    key: 0,
    class: "info-item highlight"
};
const _hoisted_11 = { class: "text" };
const _hoisted_12 = {
    key: 0,
    class: "time"
};
const _hoisted_13 = { class: "action-buttons" };
const _hoisted_14 = ["disabled"];
const _hoisted_15 = {
    key: 0,
    class: "spinner"
};
const _hoisted_16 = {
    key: 1,
    class: "fas fa-map-marker-alt"
};
const _hoisted_17 = { class: "external-links" };
const _hoisted_18 = ["href"];
const _hoisted_19 = ["href"];
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { patchLeafletForVueLifecycle } from '@/utils/leafletSafety';
export default /*@__PURE__*/ _defineComponent({
    __name: 'GastronomyHowToArriveSection',
    props: {
        latitude: {},
        longitude: {},
        address: {},
        transport: {},
        pointsOfInterest: {}
    },
    emits: ["user-location", "user-location-error"],
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const props = __props;
        /* ----------------------
           Convert latitude/longitude
        ---------------------- */
        const lat = computed(() => props.latitude ? parseFloat(String(props.latitude)) : null);
        const lng = computed(() => props.longitude ? parseFloat(String(props.longitude)) : null);
        /* ----------------------
           Default values
        ---------------------- */
        const transport = computed(() => props.transport || ['Taxi desde el centro', 'Bus urbano cercano']);
        const pointsOfInterest = computed(() => props.pointsOfInterest || []);
        /* ----------------------
           User Location
        ---------------------- */
        const userLat = ref(null);
        const userLng = ref(null);
        const userDistanceKm = ref(null);
        const estimatedTime = ref(null);
        const loadingLocation = ref(false);
        // Distancias calculadas desde la ubicación del usuario
        const computedTransport = computed(() => {
            if (!userLat.value || !userLng.value || !lat.value || !lng.value) {
                return transport.value;
            }
            const distance = userDistanceKm.value || 0;
            const timeMinutes = Math.round(distance * 1.5); // Aproximación: 1.5 min/km en taxi
            return [
                `A ${timeMinutes} min del centro en taxi`,
                ...transport.value.slice(1) // Mantener otros transportes
            ];
        });
        const computedPointsOfInterest = computed(() => {
            if (!userLat.value || !userLng.value) {
                return pointsOfInterest.value.map(p => ({
                    ...p,
                    distanceKm: p.distanceKm
                }));
            }
            // Calcular distancias desde la ubicación del usuario
            return pointsOfInterest.value.map(poi => {
                if (poi.latitude && poi.longitude) {
                    const distance = haversineDistance(userLat.value, userLng.value, poi.latitude, poi.longitude);
                    return {
                        ...poi,
                        distanceKm: distance
                    };
                }
                return poi;
            });
        });
        const getUserLocation = () => {
            if (!navigator.geolocation) {
                alert("Tu navegador no soporta geolocalización.");
                return;
            }
            loadingLocation.value = true;
            navigator.geolocation.getCurrentPosition(async (pos) => {
                userLat.value = pos.coords.latitude;
                userLng.value = pos.coords.longitude;
                emit('user-location', { latitude: userLat.value, longitude: userLng.value });
                if (lat.value && lng.value) {
                    userDistanceKm.value = haversineDistance(userLat.value, userLng.value, lat.value, lng.value);
                    // Calcular tiempo estimado (aproximación: velocidad promedio 40 km/h en ciudad)
                    const timeMinutes = Math.round(userDistanceKm.value * 1.5);
                    estimatedTime.value = `~${timeMinutes} min en auto`;
                    // Actualizar mapa con ruta
                    await nextTick();
                    updateMapWithRoute();
                }
                loadingLocation.value = false;
            }, (err) => {
                alert("No se pudo obtener tu ubicación.");
                emit('user-location-error', err);
                loadingLocation.value = false;
            });
        };
        /* ----------------------
           Google Maps fallback
        ---------------------- */
        const googleUrl = computed(() => {
            if (lat.value != null && lng.value != null)
                return `https://www.google.com/maps?q=${lat.value},${lng.value}`;
            if (props.address)
                return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(props.address)}`;
            return 'https://www.google.com/maps';
        });
        /* ----------------------
           OpenStreetMap URL
        ---------------------- */
        const osmUrl = computed(() => {
            if (!lat.value || !lng.value)
                return null;
            if (userLat.value && userLng.value) {
                // Ruta desde ubicación del usuario
                return `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${userLat.value},${userLng.value};${lat.value},${lng.value}`;
            }
            // Solo punto del destino
            return `https://www.openstreetmap.org/?mlat=${lat.value}&mlon=${lng.value}#map=17/${lat.value}/${lng.value}`;
        });
        /* ----------------------
           Distance calculator (Haversine)
        ---------------------- */
        function haversineDistance(lat1, lon1, lat2, lon2) {
            const R = 6371; // km
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLon = (lon2 - lon1) * Math.PI / 180;
            const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(lat1 * Math.PI / 180) *
                    Math.cos(lat2 * Math.PI / 180) *
                    Math.sin(dLon / 2) ** 2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        }
        /* ----------------------
           Format distance
        ---------------------- */
        function formatDistance(km) {
            if (km === null)
                return '-';
            if (km < 1)
                return `${(km * 1000).toFixed(0)} m`;
            return `${km.toFixed(2)} km`;
        }
        /* ----------------------
           Map integration (Leaflet)
        ---------------------- */
        const mapContainer = ref(null);
        let mapInstance = null;
        let destinationMarker = null;
        let userMarker = null;
        let routeLayer = null;
        const loadLeaflet = () => {
            return new Promise((resolve) => {
                if (window.L) {
                    patchLeafletForVueLifecycle(window.L);
                    resolve();
                    return;
                }
                // Cargar CSS
                const cssId = 'leaflet-css';
                if (!document.getElementById(cssId)) {
                    const link = document.createElement('link');
                    link.id = cssId;
                    link.rel = 'stylesheet';
                    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
                    document.head.appendChild(link);
                }
                // Cargar JS
                const scriptId = 'leaflet-js';
                if (document.getElementById(scriptId)) {
                    const existing = document.getElementById(scriptId);
                    existing.addEventListener('load', () => resolve(), { once: true });
                    if (window.L) {
                        patchLeafletForVueLifecycle(window.L);
                        resolve();
                    }
                    return;
                }
                const script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                script.async = true;
                script.onload = () => {
                    patchLeafletForVueLifecycle(window.L);
                    resolve();
                };
                document.body.appendChild(script);
            });
        };
        const initMap = async () => {
            if (!mapContainer.value || !lat.value || !lng.value)
                return;
            await loadLeaflet();
            const L = window.L;
            if (!L)
                return;
            patchLeafletForVueLifecycle(L);
            // Limpiar mapa anterior si existe
            if (mapInstance) {
                destroyMap();
            }
            // Crear mapa centrado en el destino
            mapInstance = L.map(mapContainer.value, {
                zoomControl: false,
                scrollWheelZoom: false,
                touchZoom: false,
                doubleClickZoom: false,
                boxZoom: false,
                keyboard: false,
                zoomAnimation: false,
                markerZoomAnimation: false,
                fadeAnimation: false
            }).setView([lat.value, lng.value], 14, { animate: false, noMoveStart: true });
            // Agregar capa de tiles
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            }).addTo(mapInstance);
            // Marcar destino
            destinationMarker = L.marker([lat.value, lng.value])
                .addTo(mapInstance)
                .bindPopup(props.address || 'Destino')
                .openPopup();
            // Si hay ubicación del usuario, mostrar ruta
            if (userLat.value && userLng.value) {
                updateMapWithRoute();
            }
        };
        const updateMapWithRoute = async () => {
            if (!mapInstance || !userLat.value || !userLng.value || !lat.value || !lng.value)
                return;
            const L = window.L;
            if (!L)
                return;
            // Remover marcador y ruta anteriores
            if (userMarker) {
                try {
                    userMarker.closePopup && userMarker.closePopup();
                }
                catch { }
                try {
                    userMarker.unbindPopup && userMarker.unbindPopup();
                }
                catch { }
                try {
                    userMarker.off && userMarker.off();
                }
                catch { }
                try {
                    mapInstance.removeLayer(userMarker);
                }
                catch { }
                userMarker = null;
            }
            if (routeLayer) {
                try {
                    routeLayer.off && routeLayer.off();
                }
                catch { }
                try {
                    mapInstance.removeLayer(routeLayer);
                }
                catch { }
                routeLayer = null;
            }
            // Agregar marcador de usuario
            userMarker = L.marker([userLat.value, userLng.value], {
                icon: L.icon({
                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                })
            })
                .addTo(mapInstance)
                .bindPopup('Tu ubicación');
            // Obtener ruta usando OSRM
            try {
                const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${userLng.value},${userLat.value};${lng.value},${lat.value}?overview=full&geometries=geojson`);
                const data = await response.json();
                if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
                    const route = data.routes[0];
                    const geometry = route.geometry;
                    // Crear polyline de la ruta
                    routeLayer = L.geoJSON(geometry, {
                        style: {
                            color: '#007bff',
                            weight: 4,
                            opacity: 0.7
                        }
                    }).addTo(mapInstance);
                    // Ajustar vista para mostrar toda la ruta
                    const bounds = routeLayer.getBounds();
                    mapInstance.fitBounds(bounds, { padding: [50, 50], animate: false });
                    // Actualizar tiempo estimado con datos reales
                    const durationSeconds = route.duration;
                    const durationMinutes = Math.round(durationSeconds / 60);
                    estimatedTime.value = `~${durationMinutes} min en auto`;
                }
            }
            catch (error) {
                console.error('Error obteniendo ruta:', error);
                // Dibujar línea recta como fallback
                routeLayer = L.polyline([[userLat.value, userLng.value], [lat.value, lng.value]], { color: '#007bff', weight: 3, opacity: 0.5, dashArray: '10, 10' }).addTo(mapInstance);
            }
        };
        const destroyMap = () => {
            try {
                if (destinationMarker) {
                    try {
                        destinationMarker.closePopup && destinationMarker.closePopup();
                    }
                    catch { }
                    try {
                        destinationMarker.unbindPopup && destinationMarker.unbindPopup();
                    }
                    catch { }
                    try {
                        destinationMarker.off && destinationMarker.off();
                    }
                    catch { }
                    try {
                        destinationMarker.remove && destinationMarker.remove();
                    }
                    catch { }
                    destinationMarker = null;
                }
                if (userMarker) {
                    try {
                        userMarker.closePopup && userMarker.closePopup();
                    }
                    catch { }
                    try {
                        userMarker.unbindPopup && userMarker.unbindPopup();
                    }
                    catch { }
                    try {
                        userMarker.off && userMarker.off();
                    }
                    catch { }
                    try {
                        userMarker.remove && userMarker.remove();
                    }
                    catch { }
                    userMarker = null;
                }
                if (routeLayer) {
                    try {
                        routeLayer.off && routeLayer.off();
                    }
                    catch { }
                    try {
                        routeLayer.remove && routeLayer.remove();
                    }
                    catch { }
                    routeLayer = null;
                }
                if (mapInstance) {
                    try {
                        mapInstance.closePopup && mapInstance.closePopup();
                    }
                    catch { }
                    try {
                        mapInstance.closeTooltip && mapInstance.closeTooltip();
                    }
                    catch { }
                    try {
                        mapInstance.off && mapInstance.off();
                    }
                    catch { }
                    try {
                        mapInstance._handlers && mapInstance._handlers.forEach((handler) => handler.disable && handler.disable());
                    }
                    catch { }
                    try {
                        mapInstance.stop && mapInstance.stop();
                    }
                    catch { }
                    try {
                        mapInstance.remove && mapInstance.remove();
                    }
                    catch { }
                    mapInstance = null;
                }
            }
            catch { }
        };
        // Observar cambios en lat/lng para actualizar mapa
        watch([lat, lng], () => {
            if (lat.value && lng.value) {
                nextTick(() => initMap());
            }
        }, { immediate: true });
        // Observar cambios en ubicación del usuario
        watch([userLat, userLng], () => {
            if (userLat.value && userLng.value && mapInstance) {
                updateMapWithRoute();
            }
        });
        onMounted(() => {
            if (lat.value && lng.value) {
                nextTick(() => initMap());
            }
        });
        onBeforeUnmount(() => {
            destroyMap();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _cache[6] || (_cache[6] = _createElementVNode("h3", { class: "h5 mb-3" }, [
                        _createElementVNode("i", { class: "fas fa-route me-2" }),
                        _createTextVNode("Cómo llegar ")
                    ], -1)),
                    (lat.value && lng.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("div", {
                                ref_key: "mapContainer",
                                ref: mapContainer,
                                class: "map-container",
                                style: { "height": "350px", "border-radius": "8px", "overflow": "hidden", "background": "#e9ecef" }
                            }, null, 512)
                        ]))
                        : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            _cache[1] || (_cache[1] = _createElementVNode("h4", { class: "section-subtitle" }, "Transporte", -1)),
                            _createElementVNode("ul", _hoisted_6, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedTransport.value, (item, i) => {
                                    return (_openBlock(), _createElementBlock("li", {
                                        key: i,
                                        class: "info-item"
                                    }, [
                                        _cache[0] || (_cache[0] = _createElementVNode("i", { class: "fas fa-bus icon" }, null, -1)),
                                        _createElementVNode("span", _hoisted_7, _toDisplayString(item), 1)
                                    ]));
                                }), 128))
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                            _cache[5] || (_cache[5] = _createElementVNode("h4", { class: "section-subtitle" }, "Puntos de interés", -1)),
                            _createElementVNode("ul", _hoisted_9, [
                                (userDistanceKm.value)
                                    ? (_openBlock(), _createElementBlock("li", _hoisted_10, [
                                        _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-walking icon" }, null, -1)),
                                        _createElementVNode("span", _hoisted_11, [
                                            _createElementVNode("strong", null, "Estás a " + _toDisplayString(formatDistance(userDistanceKm.value)), 1),
                                            (estimatedTime.value)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(estimatedTime.value), 1))
                                                : _createCommentVNode("", true)
                                        ])
                                    ]))
                                    : _createCommentVNode("", true)
                            ]),
                            _createElementVNode("div", _hoisted_13, [
                                _createElementVNode("button", {
                                    onClick: getUserLocation,
                                    class: "btn-location",
                                    disabled: loadingLocation.value
                                }, [
                                    (loadingLocation.value)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_15))
                                        : (_openBlock(), _createElementBlock("i", _hoisted_16)),
                                    _createElementVNode("span", null, _toDisplayString(loadingLocation.value ? 'Obteniendo ubicación...' : 'Usar mi ubicación'), 1)
                                ], 8, _hoisted_14),
                                _createElementVNode("div", _hoisted_17, [
                                    (osmUrl.value)
                                        ? (_openBlock(), _createElementBlock("a", {
                                            key: 0,
                                            href: osmUrl.value,
                                            target: "_blank",
                                            rel: "noopener",
                                            class: "link-external"
                                        }, [...(_cache[3] || (_cache[3] = [
                                                _createElementVNode("i", { class: "fas fa-external-link-alt" }, null, -1),
                                                _createElementVNode("span", null, "OpenStreetMap", -1)
                                            ]))], 8, _hoisted_18))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("a", {
                                        href: googleUrl.value,
                                        target: "_blank",
                                        rel: "noopener",
                                        class: "link-external"
                                    }, [...(_cache[4] || (_cache[4] = [
                                            _createElementVNode("i", { class: "fas fa-external-link-alt" }, null, -1),
                                            _createElementVNode("span", null, "Google Maps", -1)
                                        ]))], 8, _hoisted_19)
                                ])
                            ])
                        ])
                    ])
                ])
            ]));
        };
    }
});
